import { z } from "zod";
import type { IsoAndUnixTimestamp } from "../date-time/iso-and-unix";
import { isoAndUnixZodSchema } from "../date-time/iso-and-unix";
import type { DurationInMinutes } from "../date-time/time-spans";
import { durationInMinutesZodSchema } from "../date-time/time-spans";
import type { Timezone } from "../date-time/timezone";
import { timezoneZodType, tz } from "../date-time/timezone";
import type { PageId } from "../pages/page-id";
import { pageIdZodSchema } from "../pages/page-id";
import type { PageSlug } from "../pages/page-slug";
import type { Price } from "../prices/price";
import { priceZodSchema } from "../prices/price";
import {
    languageCodeZodSchema,
    type LanguageCode,
} from "../translations/language-code";
import type { Translatable } from "../translations/translation";
import { translatableZodSchema } from "../translations/translation";
import type { TimePadding } from "../weekly-schedules/time-padding";
import { timePaddingZodSchema } from "../weekly-schedules/time-padding";
import type { WeeklySchedule } from "../weekly-schedules/weekly-schedule";
import { weeklyScheduleZodSchema } from "../weekly-schedules/weekly-schedule";
import type { WeeklyScheduleException } from "../weekly-schedules/weekly-schedule-exception";
import { weeklyScheduleExceptionZodSchema } from "../weekly-schedules/weekly-schedule-exception";
import type { Fees } from "./charge-fee";

type ProviderHome =
    | {
          type: "PAGE_ID";
          pageId: PageId;
          pageSlug?: PageSlug;
      }
    | {
          type: "URL";
          url: string;
      }
    | {
          type: "LEGACY";
      };
const providerHomeTypes = ["PAGE_ID", "URL", "LEGACY"] as const;
type ProviderHomeType = (typeof providerHomeTypes)[number];

type ProviderOptions = {
    // todo: probably move to activity level when we start with that
    /**
     * Provider accepts tips /
     * This enables UI for accepting tips from end customers in checkout
     * @type {boolean}
     */
    acceptTips?: boolean;
    /**
     * If `true` no service fee is added to purchases ever /
     * @type {boolean}
     */
    serviceFeeDisabled?: boolean;
    /**
     * When hidden, provider is not shown in the UI for anyone except
     * administrators of the provider, and when shown, UI is tweaked to show
     * that it's a hidden provider and not a public one
     * @type {boolean}
     */
    hidden?: boolean;
    /**
     * Default prep time for the provider on orders. \
     * A prep time is the time a provider needs to prepare an order for delivery. \
     * A prep time of 15 min on a product will mean that you can't get a delivery in less time than 15 min.
     * @type {DurationInMinutes}
     */
    prepTime: DurationInMinutes;
    /**
     * Temporary override of prep time. \
     * Used e.g. when a provider has to much to do and still wants to accept orders
     * but can't deliver in time of the usual prep time.
     */
    prepTimeOverride?: {
        until: IsoAndUnixTimestamp;
        prepTime: DurationInMinutes;
    } | null;

    /**
     * Minimal order amount for deliveries
     */
    deliveryOrderAmountLimit?: Price;

    /**
     * Can be set to override time on Shipping to get more control when we do a pickup at a provider.
     * @type {customPickupTime}
     */
    customPickupTime?: DurationInMinutes;
    /**
     * Are there any default message about all pickups of orders from the provider all end customers should get? \
     * E.g. anything about the pickup location, or how to get there, or how the pickup works
     */
    pickUpInstructions?: string | null;
    /**
     * A weekly schedule with the business hours for the provider
     * @type {WeeklySchedule}
     */
    businessHours: WeeklySchedule;
    /**
     * Overrides for the default business hours weekly schedule. \
     * An override is a weekly schedule that takes precedence over the default weekly schedule between a chosen start and end date.
     * @type {WeeklySchedule}
     */
    businessHourExceptions: WeeklyScheduleException[];
    /**
     * When true, orders cannot be places outside of providers business hours. \
     * _defaults to `true` when delivery chosen is `table service`_
     * @type {boolean}
     */
    disableOrderingOutsideOfBusinessHours?: boolean; // defaults to true when delivery is table service
    defaultOrderTimePadding: TimePadding;
    /**
     * Enables/disables swish as a payment provider for the provider
     * @type {boolean}
     */
    acceptSwish?: boolean;
    /**
     * Timezone for the provider, defaults to `Europe/Stockholm`
     */
    timezone?: Timezone;
    /**
     * Provider is paused and unavailable until given date and time \
     * (e.g. when a provider is closed for vacation or maintenance)
     */
    paused: {
        until: IsoAndUnixTimestamp;
        reasonTitle?: Translatable;
        reason: Translatable;
    } | null;
    /**
     * Will soon be `deprecated`,
     * Use settings.payment.provision.xxx instead when it's available
     * @type {Fees}
     */
    fees?: Fees;
    /**
     * Ability to keep track of stock balance in a light weight way
     */
    stockBalance?: {
        buyables?: boolean;
    };

    /**
     * Ability to mark order-items as delivered per item
     */
    markOrderItemsAsDelivered?: {
        buyables?: boolean;
    };

    /**
     * Shows a QR code on the ticket on the receipt page
     * for bookable products
     *
     * At the moment the QR code is lasse-maja specific data
     * @type {boolean}
     */
    qrOnBookable?: boolean;

    /**
     * If enabled, the end customer will
     * be able to ask for their order to be
     * picked up outside the door, or in case
     * of a home delivery, left outside the
     * door on delivery
     *
     * Will be set to true or false for new
     * providers, for old providers without
     * the setting (undefined), it will
     * default to `true`
     * @type {boolean}
     */
    leaveMyStuffOutsideEnabled?: boolean;

    /**
     * Default home page for the provider to redirect to when
     * the user has arrived directly to a product page for instance
     *
     * `LEGACY` is both the default and will redirect to the
     * old shop page of the provider, this is the old default behavior
     * @type {boolean}
     */
    home?: ProviderHome;

    /**
     * The languages the provider supports for translations
     * of products and other content
     * defaults to `sv`
     *
     * Swedish is always supported `sv`
     */
    languages?: LanguageCode[];

    /**
     * If enabled, the provider will use the new product pages
     * v2 instead of the old product pages
     *
     */
    usesProductPagesV2?: boolean;

    /**
     * If enabled, the provider will use the provider cart for voucher purchases instead of product cart
     */
    useProviderCartForVoucher?: boolean;
};

const homeZodDiscriminatedUnion = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("PAGE_ID"),
        pageId: pageIdZodSchema,
        pageSlug: z.string().optional() as unknown as z.ZodSchema<
            PageSlug | undefined
        >,
    }),
    z.object({
        type: z.literal("URL"),
        url: z.string().url(),
    }),
    z.object({
        type: z.literal("LEGACY"),
    }),
]);
const providerOptionsZodObject = z.object({
    acceptTips: z.boolean().optional(),
    hidden: z.boolean().optional(),
    prepTime: durationInMinutesZodSchema,
    prepTimeOverride: z
        .object({
            until: isoAndUnixZodSchema,
            prepTime: durationInMinutesZodSchema,
        })
        .nullable()
        .optional(),
    deliveryOrderAmountLimit: priceZodSchema.optional(),
    customPickupTime: durationInMinutesZodSchema.optional(),
    pickUpInstructions: z.string().optional(),
    businessHours: weeklyScheduleZodSchema,
    businessHourExceptions: z.array(weeklyScheduleExceptionZodSchema),
    disableOrderingOutsideOfBusinessHours: z.boolean().optional(),
    defaultOrderTimePadding: timePaddingZodSchema,
    acceptSwish: z.boolean().optional(),
    timezone: timezoneZodType.optional().default(tz.EuropeStockholm),
    paused: z
        .object({
            until: isoAndUnixZodSchema,
            reasonTitle: translatableZodSchema.optional(),
            reason: translatableZodSchema,
        })
        .nullable(),
    fees: z
        .object({
            fixedFee: z.number().optional(),
            percentageFee: z.number().optional(),
        })
        .optional(),
    home: homeZodDiscriminatedUnion,
    languages: languageCodeZodSchema.array().optional(),
    usesProductPagesV2: z.boolean().optional(),
    useProviderCartForVoucher: z.boolean().optional(),
});
const providerOptionsZodSchema: z.ZodSchema<ProviderOptions> =
    providerOptionsZodObject;

export {
    homeZodDiscriminatedUnion,
    providerHomeTypes,
    providerOptionsZodObject,
    providerOptionsZodSchema,
};
export type { ProviderHome, ProviderHomeType, ProviderOptions };
